/**
 * PlantSpacingCalc — bed area + on-center spacing → plant count.
 *
 * Per research pain point #6: triangular packing fits ~15% more plants in
 * the same area than square grid. Most contractors quote square because
 * they don't know the formula. We default to triangular (the right answer
 * for groundcover, the most common case) and offer a toggle.
 *
 * No catalog cost lookup — plant cost varies enormously by species, so we
 * drop a Material line at $0 cost and let the contractor fill the price.
 */

import React, { useMemo, useState } from 'react';
import { useIdioma } from '../../../../i18n/idioma';
import toast from 'react-hot-toast';
import { CalculatorProps } from '../registry';
import { priceLine } from '../../../../utils/lineItemPricing';
import { NumberField, SelectField, OutputCard, OutputRow, AddToEstimateButton, SectionHeader } from '../CalculatorPrimitives';
import type { EstimateItem } from '../../../../types';

type Pattern = 'triangular' | 'square';
type AreaMode = 'rect' | 'sqft';

const SPACING_OPTIONS = [6, 9, 12, 15, 18, 24, 30, 36, 48];

/** Plants per sq ft at given on-center spacing. Triangular packs ~15.5% denser
 *  than square; numbers come from Iowa State Extension and standard nursery refs. */
function plantsPerSqFt(spacingIn: number, pattern: Pattern): number {
  if (spacingIn <= 0) return 0;
  const spacingFt = spacingIn / 12;
  if (pattern === 'square') return 1 / (spacingFt * spacingFt);
  // Triangular packing — area per plant = spacing² × √3/2
  return 1 / (spacingFt * spacingFt * (Math.sqrt(3) / 2));
}

const PlantSpacingCalc: React.FC<CalculatorProps> = ({ onAddTemplateItems, targetMarginPercent, materialMarkupPercent }) => {
    const { t } = useIdioma();
  const [areaMode, setAreaMode] = useState<AreaMode>('rect');
  const [length, setLength] = useState(0);
  const [width, setWidth] = useState(0);
  const [sqFtDirect, setSqFtDirect] = useState(0);
  const [spacingIn, setSpacingIn] = useState(18);
  const [pattern, setPattern] = useState<Pattern>('triangular');

  const calc = useMemo(() => {
    const sqFt = areaMode === 'rect' ? (length || 0) * (width || 0) : (sqFtDirect || 0);
    const density = plantsPerSqFt(spacingIn, pattern);
    const plantCount = sqFt > 0 ? Math.ceil(sqFt * density) : 0;

    // "Next spacing up" comparison — research-recommended UI affordance.
    const idx = SPACING_OPTIONS.indexOf(spacingIn);
    const nextSpacingIn = idx >= 0 && idx < SPACING_OPTIONS.length - 1 ? SPACING_OPTIONS[idx + 1] : null;
    const nextCount = nextSpacingIn && sqFt > 0
      ? Math.ceil(sqFt * plantsPerSqFt(nextSpacingIn, pattern))
      : null;

    return { sqFt, density, plantCount, nextSpacingIn, nextCount };
  }, [areaMode, length, width, sqFtDirect, spacingIn, pattern]);

  const canAdd = calc.plantCount > 0;

  const handleAdd = () => {
    // Esta fila sale a propósito SIN costo —el toast pide teclearlo en el
    // presupuesto— pero se lleva el markup del taller puesto. Con
    // `markupPercent: 0`, al teclear el costo después `updateItem` multiplicaba
    // por (1 + 0) y las plantas se vendían AL COSTO: 120 plantas a $18 eran
    // $2.160 de venta y $0 de ganancia, bajo una insignia que decía "0%" y se
    // leía como "todavía sin margen", no como "esta fila no gana nada".
    const { rate, total, markupPercent, marginPercent } = priceLine('Material', 0, calc.plantCount, {
      targetMarginPercent,
      materialMarkupPercent,
    });
    const item: EstimateItem = {
      id: Math.random().toString(36).substr(2, 9),
      type: 'Material',
      description: `Plants — ${spacingIn}" OC ${pattern}, ${calc.sqFt.toFixed(0)} sq ft`,
      quantity: calc.plantCount,
      unit: 'EA',
      cost: 0,
      rate,
      // Aquí `rate` es 0 —todavía no hay costo— y aun así el precio base se
      // estampa: es el precio que produjo la REGLA con k = 1, y en cuanto el
      // contratista teclee el costo la fila se recalcula desde su markup, no
      // desde una base heredada de un dial que ya se movió (Parte A.6.6).
      baseRate: rate,
      markupPercent,
      marginPercent,
      total,
      calcBasis: `${calc.sqFt.toFixed(0)} sqft × ${calc.density.toFixed(2)} plants/sqft (${pattern}, ${spacingIn}" OC)`,
      isOverridden: false,
    };
    onAddTemplateItems([item], { id: 'calc.plant-spacing', name: `Plants — ${spacingIn}" OC` });
    toast.success(t('calc.plantas.agregadas', { count: calc.plantCount }));
  };

  return (
    <>
      <div className="flex items-center gap-0.5 bg-acero-100 p-0.5 rounded-xl h-10">
        <button
          onClick={() => setAreaMode('rect')}
          className={`flex-1 h-9 px-3.5 rounded-[10px] text-[13px] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${areaMode === 'rect' ? 'bg-white text-navy-950 font-semibold shadow-s1' : 'text-acero-600 hover:text-navy-950 font-medium'}`}
        >
          L × W
        </button>
        <button
          onClick={() => setAreaMode('sqft')}
          className={`flex-1 h-9 px-3.5 rounded-[10px] text-[13px] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${areaMode === 'sqft' ? 'bg-white text-navy-950 font-semibold shadow-s1' : 'text-acero-600 hover:text-navy-950 font-medium'}`}
        >
          Sq ft
        </button>
      </div>

      {areaMode === 'rect' ? (
        <div className="grid grid-cols-2 gap-3">
          <NumberField label="Length" unit="ft" value={length} onChange={setLength} />
          <NumberField label="Width" unit="ft" value={width} onChange={setWidth} />
        </div>
      ) : (
        <NumberField label="Bed area" unit="sq ft" value={sqFtDirect} onChange={setSqFtDirect} />
      )}

      <div className="grid grid-cols-2 gap-3">
        <SelectField
          label="On-center spacing"
          value={String(spacingIn)}
          onChange={(v) => setSpacingIn(parseInt(v, 10) || 12)}
          options={SPACING_OPTIONS.map(s => ({ value: String(s), label: `${s} inches` }))}
        />
        <SelectField
          label="Pattern"
          value={pattern}
          onChange={(v) => setPattern(v)}
          options={[
            { value: 'triangular', label: t('calc.patron.triangular') },
            { value: 'square', label: t('calc.patron.cuadrado') },
          ]}
        />
      </div>

      <SectionHeader title={t('calc.loQueNecesitas')} />
      <OutputCard>
        <OutputRow label="Plants" value={`${calc.plantCount}`} emphasis="primary" hint={`${calc.density.toFixed(2)} plants/sqft at ${spacingIn}" OC ${pattern}`} />
        {calc.sqFt > 0 && (
          <OutputRow label="Bed area" value={`${calc.sqFt.toFixed(0)} sq ft`} emphasis="muted" />
        )}
        {calc.nextSpacingIn && calc.nextCount !== null && calc.plantCount > 0 && (
          <OutputRow
            label={`At ${calc.nextSpacingIn}" OC instead`}
            value={`${calc.nextCount} plants`}
            emphasis="muted"
            hint={`Saves ${calc.plantCount - calc.nextCount} plants`}
          />
        )}
      </OutputCard>

      <AddToEstimateButton
        label={canAdd ? `Add ${calc.plantCount} plants to estimate` : 'Enter area to add'}
        onClick={handleAdd}
        disabled={!canAdd}
      />
    </>
  );
};

export default PlantSpacingCalc;
